import { Button } from '@telegram-apps/telegram-ui'
import { Minus, Plus } from 'lucide-react'
import { useTranslate } from 'shared/lib/i18n'

export interface StockItem {
	id: string
	name: string
	maxQuantity: number
	soldQuantity: number
	availableQuantity: number
	myQuantity: number
}

/**
 * Quantity counters for stock variants — the stock-mode counterpart of
 * {@link SizeGrid}. Each variant is bounded by its `availableQuantity`; a
 * variant with none left is disabled and reads "sold out".
 */
export function StockCounters({
	items,
	quantities,
	disabled = false,
	onChange,
}: {
	items: StockItem[]
	quantities: Map<string, number>
	disabled?: boolean
	onChange: (id: string, quantity: number) => void
}) {
	const { t } = useTranslate()
	return (
		<div className='flex flex-col gap-2'>
			{items.map((item) => {
				const selected = quantities.get(item.id) ?? 0
				const soldOut = item.availableQuantity <= 0
				return (
					<div
						key={item.id}
						className='flex items-center justify-between gap-3 rounded-xl bg-secondary/40 p-3'
					>
						<div className='min-w-0'>
							<div className='truncate text-base font-semibold text-foreground'>
								{item.name}
							</div>
							<div className='mt-0.5 text-xs text-muted-foreground'>
								{soldOut
									? t('pages.lot.soldOut')
									: t('pages.lot.available', {
											count: String(item.availableQuantity),
										})}
								{item.myQuantity > 0 && (
									<>
										{' · '}
										{t('pages.lot.yourQuantity', {
											count: String(item.myQuantity),
										})}
									</>
								)}
							</div>
						</div>

						<div className='flex shrink-0 items-center gap-2'>
							<Button
								size='s'
								mode='bezeled'
								disabled={disabled || selected <= 0}
								onClick={() => onChange(item.id, selected - 1)}
								aria-label='-'
							>
								<Minus className='size-4' />
							</Button>
							<span className='w-6 text-center text-base font-semibold tabular-nums text-foreground'>
								{selected}
							</span>
							<Button
								size='s'
								mode='bezeled'
								disabled={disabled || soldOut || selected >= item.availableQuantity}
								onClick={() => onChange(item.id, selected + 1)}
								aria-label='+'
							>
								<Plus className='size-4' />
							</Button>
						</div>
					</div>
				)
			})}
		</div>
	)
}
